// deckStyles -- shared helpers for the meeting-prep deck builder.
//
// Lane 299 (QA-8, 2026-07-22): the inline style atoms (C tokens, labelStyle,
// inputStyle, btn*, pill*) were retired when the builder moved to the Linear
// `.dk` class layer (DeckAssembler.linear.css). What remains here is (a) the
// react-select `selectStyles` -- react-select's menu portals to document.body,
// OUTSIDE the `.dk` token scope, so its colours must be literal (Linear light
// palette) rather than CSS custom properties; and (b) pure logic/format helpers
// shared by DeckAssembler + the deck components (no styling, no behavior).
// No emojis (design-system rule).

// Preview document only: html is this iframe's stack scroll owner. No app,
// rail, notes, export or unmarked V1 stylesheet receives this rule.
export function hideCanvasScrollbar(doc) {
  return doc.replace('<html', '<html data-dk-canvas-scroll="hidden"')
    .replace('</head>', '<style id="dk-canvas-scrollbar">html[data-dk-canvas-scroll="hidden"]{scrollbar-width:none}html[data-dk-canvas-scroll="hidden"]::-webkit-scrollbar{display:none}</style></head>');
}

// react-select styling -- Linear light palette, literal hex (the menu portals to
// document.body, outside `.dk`, so `var(--token)` would not resolve there).
const SEL = {
  accent: '#3a53df',
  accentSoft: 'rgba(58,83,223,.09)',
  border: '#e9eaee',
  panel: '#ffffff',
  panel2: '#f7f8fa',
  text: '#16171b',
  muted: '#8d919b',
};
export const selectStyles = {
  container: (base) => ({ ...base, width: '100%' }),
  control: (base, state) => ({
    ...base, minHeight: '40px', fontSize: '13px',
    backgroundColor: state.isFocused ? SEL.panel : SEL.panel2,
    borderColor: state.isFocused ? SEL.accent : SEL.border, borderRadius: '8px',
    boxShadow: state.isFocused ? `0 0 0 3px ${SEL.accentSoft}` : 'none',
    '&:hover': { borderColor: state.isFocused ? SEL.accent : SEL.border },
  }),
  menu: (base) => ({ ...base, zIndex: 9999, fontSize: '13px', background: SEL.panel,
    border: `1px solid ${SEL.border}`, borderRadius: '8px' }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base, fontSize: '13px',
    backgroundColor: state.isFocused ? SEL.accentSoft : SEL.panel, color: SEL.text, cursor: 'pointer',
  }),
  placeholder: (base) => ({ ...base, color: SEL.muted }),
  singleValue: (base) => ({ ...base, color: SEL.text }),
  input: (base) => ({ ...base, color: SEL.text }),
  indicatorSeparator: (base) => ({ ...base, backgroundColor: SEL.border }),
  dropdownIndicator: (base) => ({ ...base, color: SEL.muted, '&:hover': { color: SEL.text } }),
  clearIndicator: (base) => ({ ...base, color: SEL.muted, '&:hover': { color: SEL.text } }),
};

export function debounce(fn, delay) {
  let timeout;
  return (...args) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
}

export function formatSpend(value) {
  const n = Number(value) || 0;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${Math.round(n / 1e3)}K`;
  return `$${Math.round(n)}`;
}

export function searchResultToOption(r) {
  const count = Number(r.material_count) || 0;
  return {
    value: r.supplier_id,
    label: `${r.supplier_name}: ${formatSpend(r.l12m_spend)} L12M · ${count} material${count === 1 ? '' : 's'}`,
    supplier: { supplier_id: r.supplier_id, supplier_name: r.supplier_name },
  };
}

// --- single-slide preview helpers (frontend-only) ------------------------
// The rendered-index helper serves both shells. isolateSlideInDoc is V1-only as
// of Lane 1233/W6; workspace-shell V2 keeps one whole marked document and uses
// the existing frame hydrate to choose visibility. Neither path changes export.

// 0-based index of the active slide AMONG THE RENDERED slides (export omits any
// now-inactive template, so a rendered index can differ from the slide-order
// index). Returns -1 if the active slide itself is omitted (not in the export).
export function renderedIndexForActive(selected, omittedKeys, activeIdx) {
  if (activeIdx == null || activeIdx < 0 || activeIdx >= (selected || []).length) return -1;
  const omitted = new Set(omittedKeys || []);
  if (omitted.has(selected[activeIdx])) return -1;
  let k = 0;
  for (let i = 0; i < activeIdx; i++) { if (!omitted.has(selected[i])) k++; }
  return k;
}

// V1: return the export document with only the slide at renderedIndex visible. The
// injected script is a STATIC string parameterised solely by a controlled integer
// (never user text), so it adds no injection surface; the export doc itself is
// server-built and already escaped. It runs in the same allow-scripts sandbox the
// export's own scaler already uses.
export function isolateSlideInDoc(docHtml, renderedIndex) {
  const k = Number(renderedIndex);
  if (!docHtml || !Number.isInteger(k) || k < 0) return docHtml;
  const inject = '<script>(function(){var w=document.querySelectorAll(".deck-page-wrap");'
    + 'for(var i=0;i<w.length;i++){w[i].style.display=(i===' + k + '?"":"none");}})();</script>';
  return docHtml.includes('</body>') ? docHtml.replace('</body>', inject + '</body>') : docHtml + inject;
}
